// src/components/AuditLog.jsx
import React, { useState, useEffect, useCallback } from 'react';
import '../styles/AuditLog.css';
import '../styles/fleet-theme.css';
import { apiFetchJSON } from '../api/apiClient';
import FleetHeader from './FleetHeader';
import { usePageTour } from './tour/tourStore';
import { saveTextFile } from '../utils/saveFile';

// Human-friendly labels for the raw action strings stored in access_audit_log.
// Anything not listed falls back to a prettified version of the raw string.
const ACTION_LABELS = {
    login: 'Login',
    admin_created: 'Admin created',
    admin_verified: 'Admin verified',
    admin_deleted: 'Admin deleted',
    admin_status_changed: 'Admin status changed',
    admin_role_changed: 'Admin role changed',
    admin_password_reset_initiated: 'Admin password reset (initiated)',
    admin_password_reset_completed: 'Admin password reset (completed)',
    client_created: 'Client created',
    client_verified: 'Client verified',
    client_updated: 'Client updated',
    client_deleted: 'Client deleted',
    client_activated: 'Client activated',
    client_deactivated: 'Client deactivated',
    client_skip_starlink_toggled: 'Skip Starlink toggled',
    client_badge_updated: 'Client badge updated',
    client_badge_deleted: 'Client badge deleted',
    user_created: 'User created',
    user_created_by_client: 'User created (by client)',
    user_verified: 'User verified',
    user_updated: 'User updated',
    user_updated_by_client: 'User updated (by client)',
    user_deleted: 'User deleted',
    user_deleted_by_client: 'User deleted (by client)',
    user_activated: 'User activated',
    user_deactivated: 'User deactivated',
    user_activated_by_client: 'User activated (by client)',
    user_deactivated_by_client: 'User deactivated (by client)',
    password_reset_initiated: 'Password reset (initiated)',
    password_reset_completed: 'Password reset (completed)',
    router_assigned: 'Router assigned',
    router_unassigned: 'Router unassigned',
    device_added: 'Device added',
    devices_bulk_added: 'Devices bulk added',
    device_removed: 'Device removed',
    devices_cleanup: 'Devices cleanup',
    device_alerts_suppressed: 'Alerts suppressed',
    device_alerts_unsuppressed: 'Alerts re-enabled',
    importance_marked: 'Marked important',
    importance_unmarked: 'Unmarked important',
    vlan_cap_set: 'VLAN cap set',
    vlan_cap_updated: 'VLAN cap updated',
    vlan_cap_deleted: 'VLAN cap deleted',
    vlan_caps_batch: 'VLAN caps (batch)',
    wan_cap_set: 'WAN cap set',
    wan_cap_updated: 'WAN cap updated',
    wan_cap_deleted: 'WAN cap deleted',
    wan_caps_batch: 'WAN caps (batch)',
    wan_priority_updated: 'WAN priority updated',
    speedtest_auto_toggled: 'Speedtest auto toggled',
};

// The mockup groups every action into 3 severity tiers rather than 8
// categories - routine day-to-day events, config changes that affect
// behavior/limits, and security/destructive events. This mapping is a
// best-effort categorization based on what each action actually does;
// if any of these read wrong for real ops (e.g. mutes should count as
// config, logins from new IPs should escalate), it's a one-line fix
// here, just flag which ones.
const ACTION_TIER = (action) => {
    const SECURITY = new Set([
        'admin_deleted', 'admin_password_reset_initiated', 'admin_password_reset_completed',
        'client_deleted', 'client_badge_deleted',
        'user_deleted', 'user_deleted_by_client',
        'password_reset_initiated', 'password_reset_completed',
        'device_removed', 'devices_cleanup',
        'vlan_cap_deleted', 'wan_cap_deleted',
    ]);
    const CONFIG = new Set([
        'admin_created', 'admin_role_changed', 'admin_status_changed',
        'client_skip_starlink_toggled', 'client_deactivated',
        'user_deactivated', 'user_deactivated_by_client',
        'vlan_cap_set', 'vlan_cap_updated', 'vlan_caps_batch',
        'wan_cap_set', 'wan_cap_updated', 'wan_caps_batch', 'wan_priority_updated',
        'speedtest_auto_toggled',
    ]);
    if (!action) return 'routine';
    if (SECURITY.has(action)) return 'security';
    if (CONFIG.has(action)) return 'config';
    return 'routine';
};

const prettify = (raw) =>
    ACTION_LABELS[raw] ||
    (raw ? raw.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '-');

const PAGE_SIZE = 50;

function AuditLog({ user, onLogout }) {
    const [logs, setLogs] = useState([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [message, setMessage] = useState({ text: '', type: '' });

    // Filters - staged (what's in the form) vs committed (what's actually
    // fetched) so "Apply filters" commits everything at once, matching
    // 7a's single-button UX instead of auto-applying each field on change.
    const [availableActions, setAvailableActions] = useState([]);
    const [filterAction, setFilterAction] = useState('');
    const [stagedAction, setStagedAction] = useState('');
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [startDate, setStartDate] = useState('');
    const [stagedStart, setStagedStart] = useState('');
    const [endDate, setEndDate] = useState('');
    const [stagedEnd, setStagedEnd] = useState('');

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [showMobileFilters, setShowMobileFilters] = useState(false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'Unknown';
        // DB stores UTC like "2026-07-16 08:28:36" (no tz). Treat as UTC.
        const iso = timestamp.includes('T') ? timestamp : timestamp.replace(' ', 'T') + 'Z';
        const date = new Date(iso);
        if (isNaN(date.getTime())) return timestamp;
        const day = date.getDate().toString().padStart(2, '0');
        const month = date.toLocaleDateString('en-US', { month: 'short' });
        const year = date.getFullYear();
        const hh = date.getHours().toString().padStart(2, '0');
        const mm = date.getMinutes().toString().padStart(2, '0');
        const ss = date.getSeconds().toString().padStart(2, '0');
        return `${day} ${month} ${year} ${hh}:${mm}:${ss}`;
    };

    const showMessage = (text, type = 'success') => {
        setMessage({ text, type });
        setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    };

    // Load the distinct action list once, for the filter dropdown.
    useEffect(() => {
        const loadActions = async () => {
            try {
                const res = await apiFetchJSON('/api/admin/audit-log/actions');
                setAvailableActions(res.actions || []);
            } catch (e) {
                // Non-fatal: the dropdown just won't populate.
                console.error('Failed to load audit actions:', e);
            }
        };
        loadActions();
    }, []);

    const fetchLogs = useCallback(async (nextOffset = 0) => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            params.set('limit', String(PAGE_SIZE));
            params.set('offset', String(nextOffset));
            if (filterAction) params.set('action', filterAction);
            if (search) params.set('search', search);
            if (startDate) params.set('start', startDate);
            // make the end date inclusive of the whole day
            if (endDate) params.set('end', endDate + ' 23:59:59');

            const res = await apiFetchJSON(`/api/admin/audit-log?${params.toString()}`);
            setLogs(res.logs || []);
            setTotal(res.total || 0);
            setOffset(nextOffset);
            setLastUpdated(formatTimestamp(new Date().toISOString()));
        } catch (error) {
            console.error('Error fetching audit log:', error);
            showMessage('Failed to load audit log: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    }, [filterAction, search, startDate, endDate]);

    // Refetch whenever a committed filter changes.
    useEffect(() => {
        fetchLogs(0);
    }, [fetchLogs]);

    const handleApplyFilters = () => {
        setFilterAction(stagedAction);
        setSearch(searchInput.trim());
        setStartDate(stagedStart);
        setEndDate(stagedEnd);
    };
    const handleSearchKey = (e) => {
        if (e.key === 'Enter') handleApplyFilters();
    };

    const handleClearFilters = () => {
        setStagedAction('');
        setFilterAction('');
        setSearch('');
        setSearchInput('');
        setStagedStart('');
        setStartDate('');
        setStagedEnd('');
        setEndDate('');
    };

    const handleLogout = async () => {
        if (!window.confirm('Are you sure you want to logout?')) return;
        if (onLogout) {
            onLogout();
            return;
        }
        try {
            await apiFetchJSON('/api/auth/logout', { method: 'POST' });
            window.location.href = '/login';
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    // No backend export endpoint confirmed, so this builds a CSV from
    // whatever's actually loaded client-side (the current page of
    // results) rather than a full server-side export of every matching
    // row across all pages.
    const handleExportCsv = async () => {
        const headers = ['Time', 'Action', 'Details', 'Serial', 'Client', 'User', 'IP Address'];
        const rows = logs.map((log) => [
            formatTimestamp(log.timestamp),
            prettify(log.action),
            log.details || '',
            log.serial_number || '',
            log.client_id != null ? log.client_id : '',
            log.user_id != null ? log.user_id : '',
            log.ip_address || '',
        ]);
        const escape = (v) => `"${String(v).replace(/"/g, '""')}"`;
        const csv = [headers, ...rows].map((r) => r.map(escape).join(',')).join('\n');
        // Browser: normal download. iOS / Android app: cache file + share sheet.
        // BOM so Excel opens accented text as UTF-8.
        try {
            await saveTextFile('\uFEFF' + csv, `audit-log-${new Date().toISOString().split('T')[0]}.csv`, {
                dialogTitle: 'Export audit log',
            });
        } catch (e) {
            console.error('Audit log export failed:', e);
            alert('Could not export the CSV. Please try again.');
        }
    };

    const hasActiveFilters = filterAction || search || startDate || endDate;
    const page = Math.floor(offset / PAGE_SIZE) + 1;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    // Group the currently-loaded page into date buckets for the
    // "Monday 07 Sep 2026 · 14 EVENTS" separator rows.
    const dateGroups = [];
    logs.forEach((log) => {
        const d = formatTimestamp(log.timestamp).split(' ').slice(0, 3).join(' '); // "07 Sep 2026"
        const dayLabel = (() => {
            const iso = log.timestamp?.includes('T') ? log.timestamp : (log.timestamp || '').replace(' ', 'T') + 'Z';
            const dt = new Date(iso);
            if (isNaN(dt.getTime())) return d;
            return dt.toLocaleDateString('en-US', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' });
        })();
        let group = dateGroups.find((g) => g.key === d);
        if (!group) {
            group = { key: d, label: dayLabel, items: [] };
            dateGroups.push(group);
        }
        group.items.push(log);
    });

    // Tier counts - computed from what's actually loaded on this page,
    // not the full filtered set across every page (no backend endpoint
    // confirmed for that), so this is an honest "on this page" count,
    // not fabricated to match a hypothetical full-dataset total.
    const tierCounts = logs.reduce(
        (acc, log) => {
            const tier = ACTION_TIER(log.action);
            acc[tier] = (acc[tier] || 0) + 1;
            return acc;
        },
        { routine: 0, config: 0, security: 0 }
    );
    const uniqueDayCount = dateGroups.length;

    // ---- Guided tour ("?" in FleetHeader) ---------------------------------
    const tourFirstLog = dateGroups[0]?.items[0] || null; // first record on screen
    usePageTour('audit-log', {
        isMobile,
        total,
        tierCounts,
        firstLog: tourFirstLog && {
            label: prettify(tourFirstLog.action),
            tier: ACTION_TIER(tourFirstLog.action),
        },
        onTourStart: () => setShowMobileFilters(false),
    }, { ready: !loading });

    return (
        <div className="audit-log">
            <FleetHeader
                title="Action Audit Log"
                user={user}
                onLogout={handleLogout}
                scope="admin"
            />

            <div className="aud-page">
                {message.text && (
                    <div className={`ft-toolbar-${message.type === 'error' ? 'error' : 'success'}`} role="alert">
                        <i className={`fas fa-${message.type === 'error' ? 'exclamation-triangle' : 'check-circle'} me-2`}></i>
                        {message.text}
                        <button type="button" className="btn-close" onClick={() => setMessage({ text: '', type: '' })}></button>
                    </div>
                )}

                {/* ── FILTER TOOLBAR (desktop) ───────────────────────────────── */}
                {!isMobile && (
                    <div className="aud-toolbar" data-tour="au-filters">
                        <div className="aud-toolbar-field" style={{ width: 230 }}>
                            <label htmlFor="filterAction">ACTION TYPE</label>
                            <select
                                id="filterAction"
                                className="aud-select"
                                value={stagedAction}
                                onChange={(e) => setStagedAction(e.target.value)}
                            >
                                <option value="">ALL ACTIONS</option>
                                {availableActions.map((a) => (
                                    <option key={a.action} value={a.action}>
                                        {prettify(a.action)} ({a.count})
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="aud-toolbar-field" style={{ flex: 1 }}>
                            <label htmlFor="search">SEARCH USER, IP, SERIAL OR DETAILS</label>
                            <div className="aud-search-box">
                                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="rgba(255,255,255,.45)" strokeWidth="1.6">
                                    <circle cx="7" cy="7" r="4.6"></circle>
                                    <path d="M10.4 10.4L14 14"></path>
                                </svg>
                                <input
                                    id="search"
                                    type="text"
                                    placeholder="e.g. matt@twenty4seven.io"
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    onKeyDown={handleSearchKey}
                                />
                            </div>
                        </div>

                        <div className="aud-toolbar-field" style={{ width: 150 }}>
                            <label htmlFor="startDate">FROM</label>
                            <input
                                id="startDate"
                                type="date"
                                className="aud-select"
                                value={stagedStart}
                                onChange={(e) => setStagedStart(e.target.value)}
                            />
                        </div>

                        <div className="aud-toolbar-field" style={{ width: 150 }}>
                            <label htmlFor="endDate">TO</label>
                            <input
                                id="endDate"
                                type="date"
                                className="aud-select"
                                value={stagedEnd}
                                onChange={(e) => setStagedEnd(e.target.value)}
                            />
                        </div>

                        <button className="aud-apply-btn" onClick={handleApplyFilters}>Apply filters</button>
                        <button className="aud-export-btn" onClick={handleExportCsv} disabled={logs.length === 0}>EXPORT CSV</button>
                        {hasActiveFilters && (
                            <button className="aud-clear-btn" onClick={handleClearFilters}>CLEAR</button>
                        )}
                    </div>
                )}

                {/* ── FILTER TOOLBAR (mobile) ─────────────────────────────────── */}
                {isMobile && (
                    <div className="aud-mobile-toolbar" data-tour="au-filters">
                        <div className="aud-mobile-search">
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="rgba(255,255,255,.45)" strokeWidth="1.6">
                                <circle cx="7" cy="7" r="4.6"></circle>
                                <path d="M10.4 10.4L14 14"></path>
                            </svg>
                            <input
                                type="text"
                                placeholder="user, IP, serial"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyDown={handleSearchKey}
                            />
                        </div>
                        <button
                            className={`aud-mobile-filter-btn ${showMobileFilters ? 'active' : ''}`}
                            onClick={() => setShowMobileFilters((s) => !s)}
                        >
                            FILTERS
                            {hasActiveFilters && (
                                <span className="aud-mobile-filter-count">
                                    {[filterAction, search, startDate, endDate].filter(Boolean).length}
                                </span>
                            )}
                        </button>
                    </div>
                )}

                {isMobile && showMobileFilters && (
                    <div className="aud-mobile-filter-panel">
                        <div className="aud-toolbar-field">
                            <label htmlFor="filterActionM">ACTION TYPE</label>
                            <select
                                id="filterActionM"
                                className="aud-select"
                                value={stagedAction}
                                onChange={(e) => setStagedAction(e.target.value)}
                            >
                                <option value="">ALL ACTIONS</option>
                                {availableActions.map((a) => (
                                    <option key={a.action} value={a.action}>
                                        {prettify(a.action)} ({a.count})
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="aud-date-row">
                            <div className="aud-toolbar-field">
                                <label htmlFor="startDateM">FROM</label>
                                <input id="startDateM" type="date" className="aud-select" value={stagedStart} onChange={(e) => setStagedStart(e.target.value)} />
                            </div>
                            <div className="aud-toolbar-field">
                                <label htmlFor="endDateM">TO</label>
                                <input id="endDateM" type="date" className="aud-select" value={stagedEnd} onChange={(e) => setStagedEnd(e.target.value)} />
                            </div>
                        </div>
                        <div className="aud-mobile-filter-actions">
                            <button className="aud-clear-btn" onClick={handleClearFilters}>CLEAR</button>
                            <button className="aud-apply-btn" onClick={() => { handleApplyFilters(); setShowMobileFilters(false); }}>Apply filters</button>
                        </div>
                    </div>
                )}

                {/* ── ACTIVITY SUMMARY ────────────────────────────────────────── */}
                <div className="aud-activity-row" data-tour="au-activity">
                    {!isMobile ? (
                        <>
                            <span className="aud-activity-label">ACTIVITY</span>
                            <span className="aud-activity-count">{total} records · {uniqueDayCount} day{uniqueDayCount === 1 ? '' : 's'}</span>
                            <div className="aud-tier-legend">
                                <span className="aud-tier-legend-item"><span className="aud-tier-dot routine" />ROUTINE {tierCounts.routine}</span>
                                <span className="aud-tier-legend-item"><span className="aud-tier-dot config" />CONFIG {tierCounts.config}</span>
                                <span className="aud-tier-legend-item"><span className="aud-tier-dot security" />SECURITY &amp; DESTRUCTIVE {tierCounts.security}</span>
                            </div>
                        </>
                    ) : (
                        <>
                            <span className="aud-activity-label">{total} RECORDS</span>
                            <span className="aud-activity-count">
                                {startDate ? new Date(startDate).toLocaleDateString('en-US', { day: '2-digit', month: 'short' }).toUpperCase() : ''}
                                {startDate && endDate ? '–' : ''}
                                {endDate ? new Date(endDate).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase() : (!startDate ? 'ALL TIME' : '')}
                            </span>
                        </>
                    )}
                </div>

                {/* ── RESULTS ─────────────────────────────────────────────────── */}
                <div className="aud-results">
                    {loading ? (
                        <div className="aud-empty">Loading activity...</div>
                    ) : logs.length === 0 ? (
                        <div className="aud-empty">
                            No activity found{hasActiveFilters ? ' for these filters.' : '.'}
                        </div>
                    ) : isMobile ? (
                        // Mobile: date-grouped card list
                        <div className="aud-mobile-list">
                            {dateGroups.map((group) => (
                                <React.Fragment key={group.key}>
                                    <div className="aud-date-group">
                                        <span>{group.label.replace(/^(\w{3})\w+ /, '$1 ')}</span>
                                        <span className="aud-date-group-count">{group.items.length} EVENTS</span>
                                    </div>
                                    {group.items.map((log) => {
                                        const tier = ACTION_TIER(log.action);
                                        return (
                                            <div className="aud-mobile-card" key={log.log_id} data-tour={log === tourFirstLog ? 'au-first' : undefined}>
                                                <div className="aud-mobile-card-top">
                                                    <span className={`aud-tier-chip ${tier}`}>{prettify(log.action)}</span>
                                                    <span className="aud-mobile-time">{formatTimestamp(log.timestamp).split(' ').slice(-1)[0]}</span>
                                                </div>
                                                {log.details && <div className="aud-mobile-details">{log.details}</div>}
                                                <div className="aud-mobile-meta">
                                                    {(log.serial_number || log.client_id != null) && (
                                                        <span>{log.serial_number || `C${log.client_id}`}</span>
                                                    )}
                                                    {log.ip_address && <span>{log.ip_address}</span>}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </React.Fragment>
                            ))}
                        </div>
                    ) : (
                        // Desktop: CSS grid with date-group separators
                        <div className="aud-grid-wrap">
                            <div className="aud-grid-header">
                                <div>TIME</div><div>ACTION</div><div>DETAILS</div><div>SERIAL</div><div>CLIENT / USER</div><div style={{ textAlign: 'right' }}>IP ADDRESS</div>
                            </div>
                            {dateGroups.map((group) => (
                                <React.Fragment key={group.key}>
                                    <div className="aud-date-group">
                                        <span>{group.label}</span>
                                        <span className="aud-date-group-count">{group.items.length} EVENTS</span>
                                    </div>
                                    {group.items.map((log, i) => {
                                        const tier = ACTION_TIER(log.action);
                                        return (
                                            <div className={'aud-grid-row' + (i % 2 === 1 ? ' aud-grid-row-alt' : '')} key={log.log_id} data-tour={log === tourFirstLog ? 'au-first' : undefined}>
                                                <div className="aud-time">{formatTimestamp(log.timestamp).split(' ').slice(-1)[0]}</div>
                                                <div><span className={`aud-tier-chip ${tier}`}>{prettify(log.action)}</span></div>
                                                <div className="aud-details">{log.details || '-'}</div>
                                                <div className="aud-serial">{log.serial_number || '—'}</div>
                                                <div className="aud-client-user">
                                                    {log.client_id != null ? `C${log.client_id}` : '—'} · {log.user_id != null ? `U${log.user_id}` : '—'}
                                                </div>
                                                <div className="aud-ip">{log.ip_address || '-'}</div>
                                            </div>
                                        );
                                    })}
                                </React.Fragment>
                            ))}
                        </div>
                    )}

                    {/* Pagination (desktop) / Load more (mobile) */}
                    {!loading && total > PAGE_SIZE && (
                        isMobile ? (
                            <div className="aud-load-more-bar">
                                <button
                                    className="aud-load-more-btn"
                                    disabled={offset + PAGE_SIZE >= total}
                                    onClick={() => fetchLogs(offset + PAGE_SIZE)}
                                >
                                    LOAD {Math.min(PAGE_SIZE, total - offset - PAGE_SIZE)} MORE
                                </button>
                            </div>
                        ) : (
                            <div className="aud-pagination">
                                <span className="aud-pagination-count">
                                    SHOWING {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} OF {total}
                                </span>
                                <div className="aud-pagination-controls">
                                    <button
                                        className="aud-page-btn"
                                        disabled={offset === 0}
                                        onClick={() => fetchLogs(Math.max(0, offset - PAGE_SIZE))}
                                    >
                                        PREV
                                    </button>
                                    <span className="aud-page-btn active">{page}</span>
                                    <button
                                        className="aud-page-btn next"
                                        disabled={offset + PAGE_SIZE >= total}
                                        onClick={() => fetchLogs(offset + PAGE_SIZE)}
                                    >
                                        NEXT
                                    </button>
                                </div>
                            </div>
                        )
                    )}
                </div>
            </div>
        </div>
    );
}

export default AuditLog;
